import { canDo, SignatureAction } from 'src/types/signatureContext';

/**
 * BRK-2059: gate + navigation-state logic for the compliance on-behalf signing affordance
 * (epic BRK-2055), kept as pure functions so the enable matrix is testable without mounting
 * the Signature Pending table.
 *
 * A `COMPLIANCE_SIGN_ON_BEHALF` holder signs on behalf of the treating therapist (therapist
 * axis) or the ASSIGNED supervisor (supervisor axis) — any discipline, so unlike the legacy
 * DOR gate there is deliberately NO `document.type === discipline` check. The backend computes
 * `CAN_COMPLIANCE_SIGN_ON_BEHALF` per row in lock-step with its own sign-time authorization
 * (`ComplianceOnBehalfAccessPolicy`), so the affordance never surfaces an action the server
 * would refuse.
 */

/** The IamScopeKey the backend mints (restore-core catalog v31). */
export const COMPLIANCE_SIGN_ON_BEHALF_SCOPE = 'COMPLIANCE_SIGN_ON_BEHALF';

export type ProxySignAxis = 'THERAPIST' | 'SUPERVISOR';

/**
 * Router-state discriminator for the signature-preview page, carried under the existing
 * `isProxySignature` nav-state key (which the legacy DOR flow populates with the bare string
 * 'PROXY_SIGNATURE' — kept untouched, normalized by {@link resolveProxySignNavState}).
 */
export interface ProxySignNavState {
  axis: ProxySignAxis;
  source: 'DOR' | 'COMPLIANCE';
  /** The provider being signed FOR: the treating therapist, or the assigned supervisor. */
  onBehalfOfProviderId: string | null;
}

/**
 * Which axis the compliance action applies to on this row, therapist-first when both
 * (mirrors the THIN-7346 axis-precedence rule on the preview page).
 */
export function getComplianceProxyAxis(document: any): ProxySignAxis | null {
  const ctx = document?.signatureContext;
  if (canDo(ctx?.allowedTherapistSignatureActions, SignatureAction.CAN_COMPLIANCE_SIGN_ON_BEHALF)) {
    return 'THERAPIST';
  }
  if (
    canDo(ctx?.allowedSupervisorSignatureActions, SignatureAction.CAN_COMPLIANCE_SIGN_ON_BEHALF)
  ) {
    return 'SUPERVISOR';
  }
  return null;
}

/**
 * The kebab-item enable rule. Fails closed while the Split is still loading (null). The Reform
 * signature preview is unconditional since THIN-7866 (the legacy Jotform surface and its
 * THIN-5503 toggle were removed), so the proxy flow always has a live sign path.
 */
export function isComplianceSignOnBehalfEnabled(args: {
  complianceSplitOn: boolean | null;
  hasComplianceScope: boolean;
  document: any;
}): boolean {
  return (
    args.complianceSplitOn === true &&
    args.hasComplianceScope &&
    getComplianceProxyAxis(args.document) !== null
  );
}

/**
 * BRK-2085: the compliance cosigner-assignment gate for one axis of the Select
 * Supervisor/Physician cell. Same Split + scope + backend-marker triple as
 * {@link isComplianceSignOnBehalfEnabled}, but against the dedicated
 * `CAN_COMPLIANCE_SELECT_*` actions — never the legacy `CAN_SELECT_*`, which the cell renders
 * ungated for authors/DORs and therefore cannot sit behind the kill switch. Fails closed while
 * the Split is loading (null).
 */
export function isComplianceSelectEnabled(args: {
  complianceSplitOn: boolean | null;
  hasComplianceScope: boolean;
  document: any;
  axis: 'supervisor' | 'physician';
}): boolean {
  const ctx = args.document?.signatureContext;
  const actions =
    args.axis === 'supervisor'
      ? ctx?.allowedSupervisorSignatureActions
      : ctx?.allowedPhysicianSignatureActions;
  const marker =
    args.axis === 'supervisor'
      ? SignatureAction.CAN_COMPLIANCE_SELECT_SUPERVISOR
      : SignatureAction.CAN_COMPLIANCE_SELECT_PHYSICIAN;
  return args.complianceSplitOn === true && args.hasComplianceScope && canDo(actions, marker);
}

/** The nav state a compliance on-behalf click carries to the signature-preview page. */
export function buildComplianceProxyNavState(document: any): ProxySignNavState | null {
  const axis = getComplianceProxyAxis(document);
  if (axis === null) {
    return null;
  }
  const onBehalfOfProviderId =
    axis === 'SUPERVISOR'
      ? document?.signatureContext?.supervisor?.id ?? document?.supervisorId ?? null
      : document?.providerId ?? null;
  return { axis, source: 'COMPLIANCE', onBehalfOfProviderId };
}

/**
 * Normalizes whatever rode the `isProxySignature` nav-state key: the legacy DOR string becomes
 * a therapist-axis DOR state (identical semantics to today's hardcoded branch), a typed
 * compliance object passes through, anything else means a direct (non-proxy) navigation.
 */
export function resolveProxySignNavState(navValue: any, document: any): ProxySignNavState | null {
  if (navValue === 'PROXY_SIGNATURE') {
    return { axis: 'THERAPIST', source: 'DOR', onBehalfOfProviderId: document?.providerId ?? null };
  }
  if (
    navValue &&
    typeof navValue === 'object' &&
    (navValue.axis === 'THERAPIST' || navValue.axis === 'SUPERVISOR') &&
    (navValue.source === 'DOR' || navValue.source === 'COMPLIANCE')
  ) {
    return navValue as ProxySignNavState;
  }
  //  Anything else (e.g. a stale cross-deploy history replay) is treated as a direct sign.
  return null;
}
